import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './LanguagePicker.module.css';

const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
];

interface TriggerProps {
    currentLang: { code: string; label: string };
    isOpen: boolean;
    toggle: () => void;
    ref: React.RefObject<HTMLButtonElement>;
}

interface LanguagePickerProps {
    trigger: (props: TriggerProps) => React.ReactNode;
}

export const LanguagePicker = ({ trigger }: LanguagePickerProps) => {
    const { i18n } = useTranslation();
    const [langOpen, setLangOpen] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const langButtonRef = useRef<HTMLButtonElement>(null);

    const currentLang = LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[1];

    const handleLanguageChange = (code: string) => {
        i18n.changeLanguage(code);
        setLangOpen(false);
    };

    const handleToggle = () => {
        if (!langOpen && langButtonRef.current) {
            const rect = langButtonRef.current.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
        setLangOpen(prev => !prev);
    };

    useEffect(() => {
        if (!langOpen) return;
        const handler = (e: MouseEvent) => {
            if (!langButtonRef.current?.contains(e.target as Node)) {
                setLangOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [langOpen]);

    return (
        <>
            {trigger({ currentLang, isOpen: langOpen, toggle: handleToggle, ref: langButtonRef })}

            {langOpen && createPortal(
                <div
                    className={styles.langDropdown}
                    style={{
                        position: 'absolute',
                        top: dropdownPos.top,
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        zIndex: 9999,
                    }}
                >
                    {LANGUAGES.map(lang => (
                        <button
                            key={lang.code}
                            className={styles.langOption}
                            onMouseDown={() => handleLanguageChange(lang.code)}
                        >
                            {lang.label}
                            {i18n.language === lang.code && <span className={styles.check}>✓</span>}
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    );
};